import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { DI_TOKENS } from '@constants';
import { useInjection } from '@hooks';
import { AssessmentDomainStatus, FileStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { AssessmentDetail } from '@libs/types';
import { AssessmentWorkflowStore } from '@stores';

import { useCancelDomain, useDeleteAssessment, useSaveDomain } from '../api';
import { AssessmentDocumentViewModel } from '../types';
import {
  calculateTotalFilesCount,
  DomainDetailsViewModel,
  ProgressFlowViewModel,
} from '../utils/assessment-mapper';
import { getLocalizedErrorMessage } from '../utils/error-utils';

interface UseAssessmentExitFlowProps {
  assessmentId: string;
  assessment: AssessmentDetail | undefined;
  domainDetailsViewModel: DomainDetailsViewModel | undefined | null;
  progressFlowViewModel: ProgressFlowViewModel | undefined;
  files: AssessmentDocumentViewModel[];
  noEvidenceChecked: boolean;
  isApplicable: boolean;
  needsToSave: () => boolean;
  /**
   * Shared `useSaveDomain` mutation instance so "save & exit" and "save & next"
   * report the same pending state (instead of two competing instances).
   */
  saveDomainMutation: ReturnType<typeof useSaveDomain>;
}

/**
 * Exit-modal flow: when the user clicks the Exit button while a domain has
 * unsaved evidence, surface a modal letting them save / delete / cancel
 * before leaving the workflow.
 *
 * Also owns the modal's open/close state via `AssessmentWorkflowStore` so the
 * layout (which renders the Exit button) and the page (which renders the
 * modal) stay in sync.
 */
export const useAssessmentExitFlow = ({
  assessmentId,
  assessment,
  domainDetailsViewModel,
  progressFlowViewModel,
  files,
  noEvidenceChecked,
  isApplicable,
  needsToSave,
  saveDomainMutation,
}: UseAssessmentExitFlowProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();
  const workflowStore = useInjection<AssessmentWorkflowStore>(
    DI_TOKENS.AssessmentWorkflowStore,
  );

  const cancelDomainMutation = useCancelDomain();
  const deleteAssessmentMutation = useDeleteAssessment();

  const handleExit = useCallback(() => {
    if (!domainDetailsViewModel?.id) {
      console.error(t('app.assessment.workflow.noDomainDetailsData'), domainDetailsViewModel);
      console.error(t('app.assessment.workflow.noDomainId'), domainDetailsViewModel?.id);
      return;
    }

    const hasUploadingFiles = files.some(
      (f) => f.status === FileStatus.QUEUED || f.status === FileStatus.UPLOADING,
    );
    if (hasUploadingFiles) {
      enqueueSnackbar(t('app.assessment.workflow.validation.waitForUploads'), { variant: 'warning' });
      return;
    }

    if (needsToSave()) {
      workflowStore.openExitModal();
      return;
    }

    navigate(routes.assessmentList());
  }, [
    domainDetailsViewModel,
    files,
    needsToSave,
    navigate,
    enqueueSnackbar,
    t,
    workflowStore,
  ]);

  const handleCancelExit = useCallback(() => {
    workflowStore.closeExitModal();
  }, [workflowStore]);

  const handleSaveAndExit = useCallback(async () => {
    if (!assessmentId || !domainDetailsViewModel?.id) return;

    try {
      if (needsToSave()) {
        await saveDomainMutation.mutateAsync({
          params: { assessmentId, assessmentDomainId: domainDetailsViewModel.id },
          data: { isSkipped: noEvidenceChecked, isApplicable },
        });
      }

      workflowStore.closeExitModal();
      navigate(routes.assessmentList());
    } catch (error) {
      console.error(t('app.assessment.workflow.failedToSaveAndExit'), error);
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to save progress. Please try again.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [
    assessmentId,
    domainDetailsViewModel,
    isApplicable,
    noEvidenceChecked,
    needsToSave,
    saveDomainMutation,
    workflowStore,
    navigate,
    enqueueSnackbar,
    t,
  ]);

  const handleDeleteAndExit = useCallback(async () => {
    if (!assessmentId || !domainDetailsViewModel?.id) return;

    try {
      await cancelDomainMutation.mutateAsync({
        assessmentId,
        assessmentDomainId: domainDetailsViewModel.id,
      });
      workflowStore.closeExitModal();
      navigate(routes.assessmentList());
      enqueueSnackbar(t('app.assessment.workflow.deleted'), { variant: 'success' });
    } catch (error) {
      console.error(t('app.assessment.workflow.failedToDeleteAssessment'), error);
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to delete assessment. Please try again.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [
    assessmentId,
    domainDetailsViewModel,
    cancelDomainMutation,
    workflowStore,
    navigate,
    enqueueSnackbar,
    t,
  ]);

  const exitModalStats = useMemo(() => {
    let completedCount = 0;
    if (assessment?.assessmentDomains) {
      assessment.assessmentDomains.forEach((domain) => {
        if (domain.status === AssessmentDomainStatus.COMPLETED) {
          completedCount++;
        }
      });
    }

    return {
      completionPercentage: progressFlowViewModel?.completionPercentage ?? 0,
      completedDomainsCount: completedCount,
      totalDomains: progressFlowViewModel?.totalDomains ?? 0,
      filesAttachedCount: calculateTotalFilesCount(assessment),
    };
  }, [assessment, progressFlowViewModel]);

  // Publish the modal handlers + display stats so the layout can render the
  // ExitAssessmentModal as an observer without prop-drilling from the page.
  useEffect(() => {
    workflowStore.registerExitModalHandlers({
      isLoading:
        saveDomainMutation.isPending || deleteAssessmentMutation.isPending,
      completionPercentage: exitModalStats.completionPercentage,
      completedDomainsCount: exitModalStats.completedDomainsCount,
      totalDomains: exitModalStats.totalDomains,
      filesAttachedCount: exitModalStats.filesAttachedCount,
      onCancel: handleCancelExit,
      onSaveAndExit: handleSaveAndExit,
      onDeleteAndExit: handleDeleteAndExit,
    });
    return () => workflowStore.registerExitModalHandlers(null);
  }, [
    workflowStore,
    saveDomainMutation.isPending,
    deleteAssessmentMutation.isPending,
    exitModalStats,
    handleCancelExit,
    handleSaveAndExit,
    handleDeleteAndExit,
  ]);

  return {
    isExitModalOpen: workflowStore.isExitModalOpen,
    exitModalStats,
    handleExit,
    handleCancelExit,
    handleSaveAndExit,
    handleDeleteAndExit,
    cancelDomainMutation,
    deleteAssessmentMutation,
  };
};
